import { useEffect, useId } from 'react';
import pym from 'pym.js';
import { SitecoreFetch } from 'Api/api/SitecoreApi';

interface IWidgetGeneratorProps {
  generatorUrl?: string;
  errors?: string;
}

export const GetAgencyByStatcode = async (statCode: string) => {
  const response = await SitecoreFetch('/api/sitecore/AgencyAPIAsync/GetAgencyByStatcode', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
    body: new URLSearchParams({ statCode }).toString(),
  });
  return response.Data ?? null;
};

// Content authors sometimes paste HTML-encoded urls into the rendering parameter
const decodeUrl = (url: string) => url.replace(/&amp;/g, '&');

const WidgetGenerator = ({ generatorUrl, errors }: IWidgetGeneratorProps) => {
  // pym.Parent looks up its container by id, so it must be unique per instance
  const containerId = `widget-generator-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  useEffect(() => {
    if (!generatorUrl) return;

    // pym.Parent builds its iframe inside the constructor and has no `allow` option. Set the
    // permission at creation time, before src is assigned, so the policy applies to the first
    // navigation. The generator's copy button needs clipboard-write.
    const originalCreateElement = document.createElement;
    document.createElement = function (this: Document, ...args: Parameters<Document['createElement']>) {
      const element = originalCreateElement.apply(this, args);
      if (String(args[0]).toLowerCase() === 'iframe') {
        element.setAttribute('allow', 'clipboard-write');
      }
      return element;
    } as Document['createElement'];

    let generatorFrame: any;
    try {
      generatorFrame = new pym.Parent(containerId, decodeUrl(generatorUrl), {});
    } finally {
      document.createElement = originalCreateElement;
    }

    // Receive events from the generator iframe, e.g. { agencyChanged: statCode }
    const receiveMessage = async (event: MessageEvent) => {
      const childWindow: Window | null | undefined = generatorFrame.iframe?.contentWindow;
      if (!childWindow || event.source !== childWindow) return;

      const agencyChanged = event.data?.agencyChanged;
      if (agencyChanged) {
        const agencyResult = await GetAgencyByStatcode(String(agencyChanged));
        childWindow.postMessage({ agencyResult }, event.origin);
      }
    };

    window.addEventListener('message', receiveMessage, false);

    return () => {
      window.removeEventListener('message', receiveMessage, false);
      generatorFrame.remove();
    };
  }, [containerId, generatorUrl]);

  return (
    <div className="widget-generator">
      {errors && (
        <div className="widget-generator-errors" style={{ color: 'red' }}>
          {errors}
        </div>
      )}
      <div id={containerId} className="widget-generator-frame" style={{ minHeight: '480px' }}></div>
    </div>
  );
};

export default WidgetGenerator;
